
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UseFormReturn } from "react-hook-form";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Contact } from "@/types/contacts";

interface ContactSelectorProps {
  form: UseFormReturn<any>;
}

export const ContactSelector = ({ form }: ContactSelectorProps) => {
  const { currentOrganization } = useOrganization();
  
  const { data, isLoading, error } = useQuery({
    queryKey: ["contacts-for-select", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) return [];
      
      // Direct database query - much faster than edge function
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('name', { ascending: true })
        .limit(100);
      
      if (error) {
        console.error("Error fetching contacts:", error);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!currentOrganization,
  });

  // Log for debugging
  console.log("Contacts data:", data);
  console.log("Contacts loading:", isLoading);
  console.log("Contacts error:", error);

  if (!currentOrganization) {
    return null;
  }

  return (
    <FormField
      control={form.control}
      name="contact_id"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Contact</FormLabel>
          <Select
            disabled={isLoading}
            value={field.value || ""}
            onValueChange={field.onChange}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue 
                  placeholder={isLoading ? "Loading contacts..." : "Select a contact"}
                />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {data && data.map((contact: Contact) => (
                <SelectItem key={contact.id} value={contact.id}>
                  {contact.name} {contact.company ? `(${contact.company})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
